import organizerAvatarUrl from "./assets/organizer-avatar.png";
import organizerCoverUrl from "./assets/organizer-cover.png";
import organizerGalleryOneUrl from "./assets/organizer-gallery-1.png";
import organizerGalleryTwoUrl from "./assets/organizer-gallery-2.png";

export type OrganizerRecord = Record<string, unknown>;

export interface OrganizerWorkbenchResponse {
  organization?: {
    id?: string;
    name?: string;
    status?: string;
  };
  summary?: {
    activities?: number;
    publishedActivities?: number;
    registrations?: number;
    paidRegistrations?: number;
    checkIns?: number;
    managerAuthorizations?: number;
    revenueFen?: number;
    settlementFen?: number;
  };
  homeDraft?: OrganizerHomeDraft;
  activities?: OrganizerRecord[];
  managerAuthorizations?: OrganizerRecord[];
  pendingTasks?: OrganizerRecord[];
}

export interface OrganizerHomeDraft {
  id?: string;
  organizationId: string;
  version?: number;
  status?: string;
  organizationName?: string;
  slogan?: string;
  serviceCities?: string[];
  coverFileId?: string;
  backgroundFileId?: string;
  avatarFileId?: string;
  galleryFileIds?: string[];
  coverFile?: OrganizerMediaFile;
  backgroundFile?: OrganizerMediaFile;
  avatarFile?: OrganizerMediaFile;
  galleryFiles?: OrganizerMediaFile[];
  longFormIntro?: string;
  moduleOrder?: string[];
  publicVisible?: boolean;
  submittedAt?: string;
  updatedAt?: string;
}

export interface OrganizerMediaFile {
  id: string;
  url?: string;
  reviewStatus?: string;
  reviewReason?: string;
}

const configuredApiBaseUrl = import.meta.env.VITE_API_BASE_URL as string | undefined;
const apiBaseUrl = (configuredApiBaseUrl || "https://ab-api.11aigc.com").replace(/\/$/, "");
const tokenStorageKey = "activityBangOrganizerSessionToken";
const localPreviewHosts = new Set(["localhost", "127.0.0.1", "0.0.0.0"]);
const allowLocalPreview = !configuredApiBaseUrl && localPreviewHosts.has(window.location.hostname);

export function getOrganizerToken() {
  return window.localStorage.getItem(tokenStorageKey) || "";
}

export function setOrganizerToken(token: string) {
  window.localStorage.setItem(tokenStorageKey, token);
}

export function clearOrganizerToken() {
  window.localStorage.removeItem(tokenStorageKey);
}

export async function organizerRequest<T>(path: string, options: RequestInit = {}): Promise<T> {
  const normalizedPath = path.startsWith("/") ? path : `/${path}`;
  const token = getOrganizerToken();
  const response = await fetch(`${apiBaseUrl}${normalizedPath}`, {
    ...options,
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(options.headers || {})
    }
  });
  const data = (await response.json().catch(() => ({}))) as OrganizerRecord;
  if (!response.ok) {
    const message = typeof data.error === "string" ? data.error : typeof data.message === "string" ? data.message : `HTTP ${response.status}`;
    throw new Error(message);
  }
  return data as T;
}

function authHeaders() {
  const token = getOrganizerToken();
  return token ? ({ Authorization: `Bearer ${token}` } as Record<string, string>) : ({} as Record<string, string>);
}

function mimeTypeFor(file: File) {
  if (file.type) return file.type;
  const lower = file.name.toLowerCase();
  if (lower.endsWith(".png")) return "image/png";
  if (lower.endsWith(".webp")) return "image/webp";
  return "image/jpeg";
}

function localMedia(file: File, slot: string): OrganizerMediaFile {
  return {
    id: `local_${slot}_${Date.now()}_${Math.random().toString(36).slice(2)}`,
    url: URL.createObjectURL(file),
    reviewStatus: "LOCAL_PREVIEW"
  };
}

export async function uploadOrganizerHomeMedia(organizationId: string, file: File, slot: "background" | "avatar" | "gallery"): Promise<OrganizerMediaFile> {
  if (!getOrganizerToken() && allowLocalPreview) return localMedia(file, slot);
  if (!getOrganizerToken()) throw new Error("请先填写并保存组织者会话 token，再上传主页图片");
  const formData = new FormData();
  formData.append("businessType", "ORGANIZER_HOME_MEDIA");
  formData.append("ownerType", "organization");
  formData.append("ownerId", organizationId);
  formData.append("fileName", file.name || `organizer-home-${slot}-${Date.now()}.jpg`);
  formData.append("mimeType", mimeTypeFor(file));
  formData.append("sizeBytes", String(file.size || 1));
  formData.append("file", file);

  const response = await fetch(`${apiBaseUrl}/api/files/uploads`, {
    method: "POST",
    headers: authHeaders(),
    body: formData
  });
  const data = (await response.json().catch(() => ({}))) as OrganizerRecord;
  if (!response.ok) {
    const message = typeof data.error === "string" ? data.error : `上传失败：HTTP ${response.status}`;
    throw new Error(message);
  }
  return data as unknown as OrganizerMediaFile;
}

export function fallbackWorkbench(organizationId = "org_owned"): OrganizerWorkbenchResponse {
  return {
    organization: {
      id: organizationId,
      name: "南山跑团",
      status: "CERTIFIED"
    },
    summary: {
      activities: 3,
      publishedActivities: 2,
      registrations: 286,
      paidRegistrations: 268,
      checkIns: 142,
      managerAuthorizations: 2,
      revenueFen: 1684000,
      settlementFen: 1515600
    },
    homeDraft: {
      organizationId,
      version: 3,
      status: "DRAFT",
      organizationName: "南山跑团",
      slogan: "城市轻越野和新手友好训练",
      serviceCities: ["深圳", "广州"],
      coverFileId: "file_home_background_demo",
      backgroundFileId: "file_home_background_demo",
      avatarFileId: "file_home_avatar_demo",
      backgroundFile: {
        id: "file_home_background_demo",
        url: organizerCoverUrl,
        reviewStatus: "APPROVED"
      },
      avatarFile: {
        id: "file_home_avatar_demo",
        url: organizerAvatarUrl,
        reviewStatus: "APPROVED"
      },
      galleryFileIds: ["file_home_gallery_demo_1", "file_home_gallery_demo_2"],
      galleryFiles: [
        {
          id: "file_home_gallery_demo_1",
          url: organizerGalleryOneUrl,
          reviewStatus: "APPROVED"
        },
        {
          id: "file_home_gallery_demo_2",
          url: organizerGalleryTwoUrl,
          reviewStatus: "PENDING"
        }
      ],
      longFormIntro: "专注城市轻越野、新手训练和周末户外活动。",
      moduleOrder: ["intro", "gallery", "activities"],
      publicVisible: true,
      updatedAt: new Date().toISOString()
    },
    activities: [
      {
        id: "act_river_run",
        title: "周末南山轻越野",
        status: "PUBLISHED",
        registrations: 86,
        paidRegistrations: 86,
        checkIns: 42,
        managerAuthorizations: 1
      },
      {
        id: "act_bay_walk",
        title: "深圳湾日落徒步",
        status: "PENDING_REVIEW",
        registrations: 0,
        paidRegistrations: 0,
        checkIns: 0,
        managerAuthorizations: 0
      }
    ],
    managerAuthorizations: [
      {
        id: "auth_001",
        activityTitle: "周末南山轻越野",
        managerName: "现场经理 A",
        active: true
      }
    ],
    pendingTasks: [
      {
        type: "HOME_DRAFT",
        title: "主页草稿尚未提交审核"
      }
    ]
  };
}

export async function getWorkbench(organizationId?: string) {
  if (!getOrganizerToken() && allowLocalPreview) return fallbackWorkbench(organizationId);
  const query = organizationId ? `?organizationId=${encodeURIComponent(organizationId)}` : "";
  return organizerRequest<OrganizerWorkbenchResponse>(`/api/organizer/workbench${query}`);
}

export async function getHomeDraft(organizationId?: string) {
  const fallbackOrganizationId = organizationId || "org_owned";
  if (!getOrganizerToken() && allowLocalPreview) return fallbackWorkbench(organizationId).homeDraft as OrganizerHomeDraft;
  const query = organizationId ? `?organizationId=${encodeURIComponent(organizationId)}` : "";
  const draft = await organizerRequest<OrganizerHomeDraft>(`/api/organizer/home-draft${query}`);
  return { ...draft, organizationId: draft.organizationId || fallbackOrganizationId };
}

export async function saveHomeDraft(draft: OrganizerHomeDraft) {
  if (!getOrganizerToken() && allowLocalPreview) {
    return {
      ...draft,
      coverFileId: draft.coverFileId || draft.backgroundFileId,
      id: draft.id || `local_home_${Date.now()}`,
      version: Number(draft.version || 0) + 1,
      status: "DRAFT",
      updatedAt: new Date().toISOString()
    };
  }
  return organizerRequest<OrganizerHomeDraft>("/api/organizer/home-draft", {
    method: "POST",
    body: JSON.stringify(draft)
  });
}

export async function submitHomeDraft(organizationId: string, draftId?: string) {
  if (!getOrganizerToken() && allowLocalPreview) {
    return {
      ...fallbackWorkbench(organizationId).homeDraft,
      status: "PENDING_REVIEW",
      submittedAt: new Date().toISOString()
    } as OrganizerHomeDraft;
  }
  return organizerRequest<OrganizerHomeDraft>("/api/organizer/home-draft/submit-review", {
    method: "POST",
    body: JSON.stringify({ organizationId, draftId })
  });
}

export async function listManagerAuthorizations(organizationId?: string) {
  if (!getOrganizerToken() && allowLocalPreview) return fallbackWorkbench(organizationId).managerAuthorizations || [];
  const query = organizationId ? `?organizationId=${encodeURIComponent(organizationId)}` : "";
  return organizerRequest<OrganizerRecord[]>(`/api/organizer/manager-authorizations${query}`);
}

export async function listOrganizerActivities(organizationId?: string) {
  if (!getOrganizerToken() && allowLocalPreview) return fallbackWorkbench(organizationId).activities || [];
  const query = organizationId ? `?organizationId=${encodeURIComponent(organizationId)}` : "";
  return organizerRequest<OrganizerRecord[]>(`/api/organizer/activities${query}`);
}

export async function updateOrganizerActivity(activityId: string, input: OrganizerRecord) {
  if (!getOrganizerToken() && allowLocalPreview) {
    return {
      id: activityId,
      ...input,
      updatedAt: new Date().toISOString()
    };
  }
  return organizerRequest<OrganizerRecord>(`/api/organizer/activities/${encodeURIComponent(activityId)}`, {
    method: "POST",
    body: JSON.stringify(input)
  });
}

export async function createManagerAuthorization(input: { organizationId: string; activityId: string; managerUserId: string; reason?: string }) {
  if (!getOrganizerToken() && allowLocalPreview) return { id: `local_auth_${Date.now()}`, ...input, active: true };
  return organizerRequest<OrganizerRecord>("/api/organizer/manager-authorizations", {
    method: "POST",
    body: JSON.stringify(input)
  });
}

export function formatFen(value: unknown) {
  const amount = typeof value === "number" ? value : Number(value || 0);
  return `¥${(amount / 100).toFixed(2)}`;
}

export function textValue(value: unknown, fallback = "-") {
  if (value === null || value === undefined || value === "") return fallback;
  if (Array.isArray(value)) return value.join(" / ");
  if (typeof value === "object") return JSON.stringify(value);
  return String(value);
}
